/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import invariant from 'tiny-invariant';

import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import DropIndicator from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { token } from '@atlaskit/tokens';

import { usePragmaticDragAndDrop } from '../../hooks/use-pragmatic-drag-and-drop';
import { isSameTable } from '../../utils/table-scoping';
import {
	cssPinningBackgroundLeft,
	cssPinningBackgroundRight,
	cssVarTableHeight,
} from '../../wrappers/charlie-table-dnd-wrapper';

import { HeadCell, type HeadCellProps } from './head-cell';

const hitboxWidth = '12px';

const dropTargetStyle = css({
	position: 'absolute',
	top: token('space.0'),
	left: token('space.0'),
	right: token('space.0'),
	zIndex: 400,
	display: 'none',
});

const resizerStyles = css({
	width: hitboxWidth,
	background: 'transparent',
	border: 'none',
	cursor: 'col-resize',
	flexGrow: '0',
	position: 'absolute',
	zIndex: 400,
	right: token('space.0'),
	top: token('space.0'),
	bottom: token('space.0'),
	outline: 'none',
});

const resizingStyles = css({
	// turning off the resizing cursor as sometimes it can cause the cursor to flicker
	// while resizing. The browser controls the cursor while dragging, but the browser
	// can sometimes bug out.
	cursor: 'unset',
});

const pinnedColumnLeftStyle = css({
	position: 'absolute',
	top: token('space.0'),
	right: token('space.0'),
	'&::after': {
		content: '""',
		position: 'absolute',
		right: token('space.negative.075'),
		top: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		height: `var(${cssVarTableHeight})`,
		width: '6px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		background: `var(${cssPinningBackgroundLeft})`,
	},
});

const pinnedColumnRightStyle = css({
	position: 'absolute',
	top: token('space.0'),
	left: token('space.0'),
	'&::before': {
		content: '""',
		position: 'absolute',
		left: token('space.negative.075'),
		top: '0',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		height: `var(${cssVarTableHeight})`,
		width: '6px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		background: `var(${cssPinningBackgroundRight})`,
	},
});

export type ColumnResizeMode = 'onChange' | 'onEnd' | 'onChangeEnd';

type HeaderState =
	| {
			type: 'idle';
	  }
	| {
			type: 'resizing';
			initialWidth: number;
			deltaOffset: number;
	  }
	| {
			type: 'keyboardResizing';
			initialWidth: number;
			deltaOffset: number;
	  }
	| { type: 'preview' }
	| { type: 'dragging' };

const idleState: HeaderState = { type: 'idle' };
const draggingState: HeaderState = { type: 'dragging' };
const defaultAllowedEdges: Edge[] = ['left', 'right'];
/**
 * __PragmaticHeadCell__
 *
 * EXPERIMENTAL HeadCell element that uses pragmatic dnd for certain actions. Still being worked on.
 */
export const PragmaticHeadCell = ({
	children,
	onColumnResizeDrag,
	onColumnResizeEnd,
	allowedEdges = defaultAllowedEdges,
	id,
	enableColumnReorder = false,
	enableResizing = false,
	columnResizeMode = 'onChange',
	isPinningEdge,
	size,
	resizeColumnLabel,
	resizerRef: externalResizerRef,
	onResizeHandleHoverChange,
	isDropAllowed,
	...props
}: HeadCellProps & {
	isPinningEdge?: 'right' | 'left';
	enableColumnReorder?: boolean;
	enableResizing?: boolean;
	columnResizeMode?: ColumnResizeMode;
	allowedEdges?: Edge[];
	onColumnResizeDrag: (width: number) => void;
	onColumnResizeEnd: (finalWidth?: number) => void;
	id: string;
	size?: {
		min?: number;
		max?: number;
		value?: number;
	};
	resizeColumnLabel?: string;
	resizerRef?: React.RefObject<HTMLDivElement>;
	onResizeHandleHoverChange?: (columnId: string | false) => void;
	isDropAllowed: ({
		sourceColumnId,
		targetColumnId,
	}: {
		sourceColumnId: string;
		targetColumnId: string;
	}) => boolean;
}): JSX.Element => {
	const dropTargetRef = useRef<HTMLDivElement | null>(null);
	const headRef = useRef<HTMLTableCellElement | null>(null);
	const localResizerRef = useRef<HTMLDivElement | null>(null);
	const resizerRef = externalResizerRef || localResizerRef;
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [state, setState] = useState<HeaderState>(idleState);
	usePragmaticDragAndDrop({
		enabled: enableColumnReorder,
		elementRef: headRef,
		dropTargetRef,
		allowedEdges,
		getData: () => ({ id, type: 'item-column' }),
		canDrop: ({ source }) =>
			source.data.type === 'item-column' &&
			isDropAllowed({ sourceColumnId: source.data.id as string, targetColumnId: id }),
		canMonitor: ({ source }) =>
			source.data.type === 'item-column' && isSameTable(source.element, headRef.current),
		onDragStart: () => {
			if (state.type !== 'resizing') {
				setState(draggingState);
			}
		},
		onDrag: (closestEdge, sourceElement, targetElement, sourceData) => {
			if (state.type === 'resizing') {
				return;
			}
			if (!enableColumnReorder) {
				return;
			}
			const isSource = sourceElement === targetElement;
			if (isSource) {
				closestEdge != null && setClosestEdge(null);
				return;
			}
			// Check if the drop is allowed before showing the indicator
			if (isDropAllowed({ sourceColumnId: sourceData?.id as string, targetColumnId: id })) {
				setClosestEdge(closestEdge);
			} else {
				setClosestEdge(null);
			}
		},
		onDragLeave: () => {
			setClosestEdge(null);
		},
		onDrop: () => {
			setClosestEdge(null);
		},
		onDragEnd: () => {
			setState(idleState);
		},
		dependencies: [id, state.type, closestEdge],
	});

	useEffect(() => {
		if (!enableResizing) {
			return;
		}
		const handle = resizerRef.current;
		invariant(handle);
		const header = headRef.current;
		invariant(header);

		return draggable({
			element: handle,
			getInitialData() {
				return { type: 'column-resize' };
			},
			onGenerateDragPreview({ nativeSetDragImage }) {
				disableNativeDragPreview({ nativeSetDragImage });
				preventUnhandled.start();

				const initialWidth = header.getBoundingClientRect().width;
				if (columnResizeMode === 'onEnd') {
					// Mark the column as resizing without mutating sizing state.
					onColumnResizeDrag(initialWidth);
				}
				setState({
					type: 'resizing',
					initialWidth,
					deltaOffset: 0,
				});
			},
			onDrag({ location }) {
				const diffX = location.current.input.clientX - location.initial.input.clientX;

				invariant(state.type === 'resizing');
				const { initialWidth } = state;

				const proposedWidth = initialWidth + diffX;

				if (columnResizeMode === 'onChange' || columnResizeMode === 'onChangeEnd') {
					if (proposedWidth !== initialWidth) {
						onColumnResizeDrag(proposedWidth);
					}
				} else {
					// onEnd mode: track delta offset for css transform, don't update state yet
					setState((prev) => {
						if (prev.type !== 'resizing') {
							return prev;
						}
						return {
							...prev,
							deltaOffset: diffX,
						};
					});
				}
			},
			onDrop({ location }) {
				preventUnhandled.stop();

				if (
					(columnResizeMode === 'onEnd' || columnResizeMode === 'onChangeEnd') &&
					state.type === 'resizing'
				) {
					// update the width state at the end of the resize
					const diffX = location.current.input.clientX - location.initial.input.clientX;
					const finalWidth = state.initialWidth + diffX;
					onColumnResizeEnd(finalWidth);
				} else {
					onColumnResizeEnd();
				}

				setState(idleState);
			},
		});
	}, [state, onColumnResizeDrag, onColumnResizeEnd, enableResizing, resizerRef, columnResizeMode]);

	const isResizingOrKeyboardResizing =
		state.type === 'resizing' || state.type === 'keyboardResizing';
	const shouldShowResizeTransform = isResizingOrKeyboardResizing && columnResizeMode === 'onEnd';

	return (
		<HeadCell {...props} ref={headRef}>
			{children}
			<div
				ref={dropTargetRef}
				css={dropTargetStyle}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				style={{ height: `var(${cssVarTableHeight})` }}
			>
				{closestEdge && enableColumnReorder && (
					<DropIndicator edge={closestEdge} type="no-terminal" />
				)}
			</div>
			{isPinningEdge && (
				<div
					css={[
						isPinningEdge === 'left' && pinnedColumnLeftStyle,
						isPinningEdge === 'right' && pinnedColumnRightStyle,
					]}
				/>
			)}
			{enableResizing && (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button
				<div
					ref={resizerRef}
					role="slider"
					tabIndex={0}
					aria-valuemin={size?.min}
					aria-valuemax={size?.max}
					aria-orientation="horizontal"
					aria-valuenow={size?.min && size?.max ? size?.value : undefined}
					aria-label={resizeColumnLabel}
					onMouseEnter={() => {
						onResizeHandleHoverChange?.(id);
					}}
					onMouseLeave={() => {
						onResizeHandleHoverChange?.(false);
					}}
					onFocus={() => {
						const header = headRef.current;
						if (!header) {
							return;
						}
						onColumnResizeDrag(header.getBoundingClientRect().width);
					}}
					onKeyDown={(e) => {
						const header = headRef.current;
						if (!header) {
							return;
						}

						if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
							e.preventDefault(); // avoid scrolling

							let deltaX = 10;
							if (e.key === 'ArrowLeft') {
								deltaX = -10;
							}

							if (columnResizeMode === 'onEnd') {
								if (state.type !== 'keyboardResizing') {
									const initialWidth = header.getBoundingClientRect().width;
									// Mark the column as resizing without mutating sizing state.
									onColumnResizeDrag(initialWidth);
								}
								// onEnd: keep track of changes visually, commit on enter
								setState((prev) => {
									if (prev.type === 'keyboardResizing') {
										// already keyboard resizing, add to delta
										return {
											...prev,
											deltaOffset: prev.deltaOffset + deltaX,
										};
									}
									// start keyboard resizing
									const initialWidth = header.getBoundingClientRect().width;
									return {
										type: 'keyboardResizing',
										initialWidth,
										deltaOffset: deltaX,
									};
								});
							} else {
								// onChange and onChangeEnd: update immediately
								const initialWidth = header.getBoundingClientRect().width;
								onColumnResizeDrag(initialWidth + deltaX);
							}
						}

						if (e.key === 'Enter' && state.type === 'keyboardResizing') {
							e.preventDefault();
							const finalWidth = state.initialWidth + state.deltaOffset;
							onColumnResizeEnd(finalWidth);
							setState(idleState);
						}

						if (e.key === 'Enter' && columnResizeMode === 'onChangeEnd') {
							e.preventDefault();
							const currentWidth = header.getBoundingClientRect().width;
							onColumnResizeEnd(currentWidth);
						}

						if (e.key === 'Escape' && state.type === 'keyboardResizing') {
							e.preventDefault();
							onColumnResizeEnd();
							setState(idleState);
						}
					}}
					onBlur={() => {
						// discard keyboard resize changes on blur
						if (state.type === 'keyboardResizing') {
							onColumnResizeEnd();
							setState(idleState);
							return;
						}
						onColumnResizeEnd();
					}}
					css={[
						resizerStyles,
						isResizingOrKeyboardResizing ? resizingStyles : undefined,
					]}
					/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop */
					// outline none is needed here to prevent Jira style overrides from being applied
					style={{
						height: `var(${cssVarTableHeight})`,
						outline: 'none',
						// Adjust position to compensate for border width changes (1px vs 2px)
						// This keeps the handle in the same visual position, preventing flicker
						right: `var(--col-${id}-resizer-offset, 0px)`,
						// use css transform for visual feedback during resize in 'onEnd' mode (mouse or keyboard)
						...(shouldShowResizeTransform && {
								transform: `translateX(${state.deltaOffset}px)`,
							}),
					}}
					/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop */
				/>
			)}
		</HeadCell>
	);
};
